<template>
  <div>
    <header class="page-header">
      <div class="header-text">
        OU Copacker Directory
        <div class="help-tooltip">
          <InfoTooltip
            text="Search here to find an OU manufacturer for your unique product"
            :showOnClick="true"
          >
            <font-awesome-icon icon="question-circle" />
          </InfoTooltip>
        </div>
        <span class="results-label"
          >1-<span>{{ countFacilitiesDisplayed }}</span> of
          {{ totalItemsInList }} results</span
        >
      </div>
      <div style="flex: 1">
        <div class="header-input">
          <input
            type="text"
            id="SingleBarSearch"
            name="SingleBarSearch"
            placeholder="Search for name, industry, description or location"
            class="form-control"
            v-model="txtInput"
            @keydown.enter.prevent
          />
          <!-- => Prevent enter click from doing any action      -->
          <span class="search-icon" @click="filterCoPackerSearch"
            ><svg
              fill="#666"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 30 30"
              width="20px"
              height="30px"
            >
              <path
                d="M 13 3 C 7.4889971 3 3 7.4889971 3 13 C 3 18.511003 7.4889971 23 13 23 C 15.396508 23 17.597385 22.148986 19.322266 20.736328 L 25.292969 26.707031 A 1.0001 1.0001 0 1 0 26.707031 25.292969 L 20.736328 19.322266 C 22.148986 17.597385 23 15.396508 23 13 C 23 7.4889971 18.511003 3 13 3 z M 13 5 C 17.430123 5 21 8.5698774 21 13 C 21 17.430123 17.430123 21 13 21 C 8.5698774 21 5 17.430123 5 13 C 5 8.5698774 8.5698774 5 13 5 z"
              />
            </svg>
          </span>
        </div>
        <span @click="ClearAllFilters" class="clear-input"
          ><font-awesome-icon icon="filter" /> Click here to clear all filters
          and show all</span
        >
      </div>
    </header>
    <div class="contact-text">
      Questions or comments? Contact
      <a href="mailto:CopackSupport@ou.org" style="margin-right: 10px"
        >CopackSupport@ou.org.</a
      >
      Be sure to contact your Account Executive/Rabbinic Coordinator before
      formalizing a copacking arrangement.
    </div>
    <main class="page-container">
      <div class="sidebar">
        <FilterContainer
          :list="uniqueCategories"
          title="Industry"
          @filter-update="RecalculateFilterSelections"
          @clear-filter-selections="ClearThisFilter"
        />
        <FilterContainer
          :list="uniqueLocations"
          title="Location"
          @filter-update="RecalculateFilterSelections"
          @clear-filter-selections="ClearThisFilter"
        />
      </div>
      <div class="main-content">
        <div>
          <Button
            class="add-new-facility-button"
            v-if="!showFacilityForm && canModifyFacilities"
            theme-color="primary"
            @click="addNewClicked"
          >
            Add New Facility
          </Button>
          <CopackerFacilityForm
            id="facilityForm"
            v-if="showFacilityForm && canModifyFacilities"
            :industries="facilityCategories"
            :locations="facilityLocations"
            :facility="editFormFacility"
            @closeForm="showFacilityForm = false"
          />
          <section
            class="no-results"
            v-if="
              !copackerFacilitiesLoading && copackerFacilitiesList.length === 0
            "
          >
            No results found.<br />
            Please change your filters or search term and try again.
          </section>
          <section class="results" v-else>
            <div
              class="result-item"
              v-for="c of copackerFacilitiesList"
              :key="c.id"
              :id="c.id.toString()"
            >
              <Transition name="fade">
                <div
                  v-if="c.showDeleteConfirmation && canModifyFacilities"
                  class="result-delete__overlay"
                >
                  <font-awesome-icon
                    class="result-delete__close"
                    icon="times"
                    @click="c.showDeleteConfirmation = false"
                  />
                  <span class="result-delete__content-text"
                    >Are you sure you want to remove facility
                    {{ c.company.Name }} from the Copacker Directory?</span
                  >
                  <div class="result-delete__content-actions">
                    <Button
                      class="result-delete__content-action"
                      theme-color="error"
                      type="button"
                      @click="deleteFacilityAsync(c)"
                    >
                      Delete
                    </Button>
                    <Button
                      class="result-delete__content-action"
                      theme-color="secondary"
                      type="button"
                      @click="c.showDeleteConfirmation = false"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Transition>
              <div class="result-main">
                <header>
                  <h2>{{ c.company.Name }}</h2>
                  <div class="location">
                    <!-- {{c.location}} TBD does not exist???-->
                    <span v-for="(val, index) of c.locations" :key="index">
                      {{ val.location }}, {{ val.locationCountry }}
                    </span>
                  </div>
                </header>
                <div class="description" style="font-weight: bold">
                  <div class="industry">
                    <ReadMore :text="c.description" />
                  </div>
                </div>
                <br />
                <div class="description">
                  <div class="industry">
                    <IndustriesList :facilitiesCategories="c.categories" />
                  </div>
                </div>
                <br />

                <div class="description">
                  <!-- we're forcing http over here since some of these links dont support https. and the ones that do will redirect from http             -->
                  <div class="industry">
                    <a
                      class="contact-website"
                      target="_blank"
                      :href="'http://' + c.websiteUrl"
                      >{{ c.websiteUrl }}</a
                    >
                  </div>
                </div>
              </div>
              <div class="result-contact">
                <div class="result-contact-header">
                  <span class="contact-name">Contact Info: </span>
                  <div class="result-contact-header__actions">
                    <font-awesome-icon
                      v-if="canModifyFacilities"
                      class="edit-button"
                      icon="fa-solid fa-pen-to-square"
                      @click="editClicked(c)"
                    />
                    <font-awesome-icon
                      v-if="canModifyFacilities"
                      class="delete-button"
                      icon="fa-solid fa-trash"
                      @click="c.showDeleteConfirmation = true"
                    />
                  </div>
                </div>
                <div v-if="c.contact == null">
                  <a href="mailto:coPackSupport@ou.org">coPackSupport@ou.org</a>
                </div>
                <div v-else>
                  {{ c.contact.Title }} {{ c.contact.Firstname }}
                  {{ c.contact.Lastname }}
                  <a :href="'mailto:' + c.contact.Email">{{
                    c.contact.Email
                  }}</a>
                  <span class="contact-phone">{{ c.contact.Voice }} </span>
                </div>
              </div>
            </div>

            <div
              class="more-results"
              v-if="
                !copackerFacilitiesLoading &&
                countFacilitiesDisplayed &&
                totalItemsInList &&
                countFacilitiesDisplayed < totalItemsInList
              "
            >
              <a @click="seeMoreMainGridAsync">See more ></a>
            </div>
          </section>
          <div v-if="copackerFacilitiesLoading">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              style="margin: auto; background: none; display: block"
              width="30px"
              height="30px"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid"
            >
              <circle
                cx="50"
                cy="50"
                fill="none"
                stroke="#3a5da6"
                stroke-width="10"
                r="35"
                stroke-dasharray="164.93361431346415 56.97787143782138"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  repeatCount="indefinite"
                  dur="2.941176470588235s"
                  values="0 50 50;360 50 50"
                  keyTimes="0;1"
                ></animateTransform>
              </circle>
            </svg>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { api } from "@/api/";
import type { CoPackerFacilities } from "@/Domain/CoPackerFacilities";
import { useMainStore } from "@/stores/useMainStore";
import FilterContainer from "@/shared/ui/filter-container/FilterContainer.vue";
import states from "states-us";
import IndustriesList from "./IndustriesList.vue";
import ReadMore from "@/shared/ui/read-more/read-more.vue";
import type { FilterListItem } from "@/shared/ui/filter-container/filterListItem";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import InfoTooltip from "@/shared/ui/InfoTooltip.vue";
import { Button } from "@progress/kendo-vue-buttons";
import CopackerFacilityForm from "./CopackerFacilityForm.vue";
import type { CoPackerFacilitiesLocation } from "@/Domain/CoPackerFacilitiesLocation";
import type { CoPackerFacilitiesCategory } from "@/Domain/CoPackerFacilitiesCategory";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";

const main = useMainStore();
const notificationStore = useNotificationStore();

interface CopackerFacilityExtended extends CoPackerFacilities {
  showDeleteConfirmation: boolean;
}

const txtInput = ref<string>();

let FilterItemsOperators = "";
let FilterItemsFields = "";
let FilterItemsValues = "";

const countFacilitiesDisplayed = ref<number>();
const totalItemsInList = ref<number>();
const defaultTake = 20;
let seeMoreCounterMainGrid = defaultTake; // we are getting more data for this page in iterations of 20

const copackerFacilitiesList = ref<CopackerFacilityExtended[]>([]);

const copackerFacilitiesLoading = ref<boolean>(false);

const showFacilityForm = ref<boolean>(false);
const editFormFacility = ref<CoPackerFacilities>();

const facilityCategories = ref<CoPackerFacilitiesCategory[]>([]);
const facilityLocations = ref<CoPackerFacilitiesLocation[]>([]);

const uniqueCategories = ref<FilterListItem[]>([]);
const uniqueLocations = ref<FilterListItem[]>([]);

//semi-colon delimited list of selected filter items
let CategoryFilterItemsSelected = "";
let LocationFilterItemsSelected = "";

txtInput.value =
  new URLSearchParams(window.location.search).get("search") ?? "";

const canModifyFacilities = ref<boolean>(false);

async function GetCoPackerCategoriesAndLocationsForFilters(
  skip = 0,
  take = 20
) {
  const getCategoriesPromise = api.get("GetCoPackerFacilitiesCategories", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    skip: skip,
    take: take,
  });

  const getLocationsPromise = api.get("GetCoPackerFacilitiesLocations", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    skip: skip, //on page load take the first 20 results . until phase 2 where we randomize this to rotate and feature different companies
    take: take,
  });

  await handleResponsePromiseAsync(
    getCategoriesPromise,
    (data) => {
      facilityCategories.value = data;

      uniqueCategories.value = []; //reset
      const categoryParents = ref<string[]>([]);
      data.forEach((cat) => {
        if (
          !categoryParents.value?.includes(cat.categoryParent) &&
          cat.categoryParent > ""
        )
          categoryParents.value.push(cat.categoryParent);
        else if (
          cat.categoryParent == "" &&
          cat.category != "" &&
          !categoryParents.value?.includes(cat.category)
        )
          categoryParents.value.push(cat.category);
      });

      //sort result alphabetically
      categoryParents.value.sort((a, b) => (a < b ? -1 : 1));

      for (const element of categoryParents.value) {
        const categoryChildren = ref<{ label: string; checked: boolean }[]>([]); //reset on each run
        data.forEach((cat) => {
          if (cat.categoryParent == element)
            categoryChildren.value.push({
              label: cat.categoryChild,
              checked: false,
            });
        });

        uniqueCategories.value.push({
          type: "Category",
          label: element,
          children: categoryChildren.value,
          checked: false,
        });
      }
    },
    "CopackerFacilities.vue",
    "GetCoPackerCategoriesAndLocationsForFilters"
  );

  await handleResponsePromiseAsync(
    getLocationsPromise,
    (data) => {
      facilityLocations.value = data;

      // Distinct locations by stateProvinceOrCityForLocationFilter
      data = data.filter(
        (v, i, a) =>
          a.findIndex(
            (t) =>
              t.stateProvinceOrCityForLocationFilter ==
              v.stateProvinceOrCityForLocationFilter
          ) === i
      );

      uniqueLocations.value = []; //reset

      const locationCountries = ref<string[]>([]);
      data.forEach((loc) => {
        if (!locationCountries.value?.includes(loc.locationCountry)) {
          if (loc.locationCountry > "" && loc.locationCountry != "USA") {
            locationCountries.value.push(loc.locationCountry.trim());
          }
        }
      });

      // sort alphabetically by country but push USA to the top of the list
      locationCountries.value.sort((a, b) => (a < b ? -1 : 1));

      // move USA to the top
      locationCountries.value.unshift("USA");

      for (const element of locationCountries.value) {
        // reset on each run
        const locationChildren = ref<{ label: string; checked: boolean }[]>([]);
        const unsortedChildList = data;

        unsortedChildList.forEach((loc) => {
          if (loc.locationCountry == element) {
            let locationChild = "";

            if (loc.locationState.length > 0) locationChild = loc.locationState;
            else if (loc.locationProvince.length > 0)
              locationChild = loc.locationProvince;
            else if (loc.locationCity.length > 0)
              locationChild = loc.locationCity;
            else locationChild = "";

            // replace state codes with state names
            if (loc.locationCountry == "USA") {
              locationChild =
                states.find((x) => x.abbreviation == locationChild)?.name ??
                locationChild;
            }

            locationChildren.value.push({
              label: locationChild,
              checked: false,
            });
          }
        });

        locationChildren.value.sort((a, b) => (a.label < b.label ? -1 : 1));

        uniqueLocations.value.push({
          type: "Location",
          label: element,
          children: locationChildren.value,
          checked: false,
        });
      }
    },
    "CopackerFacilities.vue",
    "GetCoPackerCategoriesAndLocationsForFilters"
  );
}

function ScrubSingleSearchBarInput(value = "") {
  let newSearchTerms = value; // add prefix space to separate from existing search terms

  //1- find state name from state code.  I dont think this is needed
  //addSearchTerms += value.split(' ').forEach(searchTerm => {(states.find(x => x.abbreviation == searchTerm)?.name)});  // add in the full name if the single bar search is NJ, for example and the reverse as well

  //2- find state code from state name
  if (value.trim().length > 0) {
    states.forEach((x) => {
      if (value.toLowerCase().includes(x.name.toLowerCase())) {
        newSearchTerms = value
          .toLowerCase()
          .replace(x.name.toLowerCase(), x.abbreviation);
        console.debug(
          "CopackerFacilities.vue: ScrubSingleSearchBarInput(): find state code from state name: " +
            x.name +
            "/" +
            x.abbreviation
        );
      }
    }); // add in the full name if the single bar search is NJ, for example and the reverse as well
  } else newSearchTerms = value;

  newSearchTerms = newSearchTerms.replace("  ", " "); //replace multiple spaces between words with single space only so we can split cleanly on spaces downstream
  return newSearchTerms.trim();
}

async function GetCoPackerFacilities(
  skip = 0,
  take = 20,
  fields = "",
  operators = "",
  values = "",
  moreResults = false
) {
  if (copackerFacilitiesLoading.value) return;

  copackerFacilitiesLoading.value = true;

  // default values are skip=0, take=20
  const getCoPackerFacilitiesPromise = api.get("GetCoPackerFacilities", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchTerms: ScrubSingleSearchBarInput(txtInput.value),
    filterItems: "", // new {string  [] },
    fields: fields, //'Description;Description',  // these test values are working but how do i actually get the checked values off the screen?
    operators: operators, //,'contains;contains', // ''
    values: values, // 'alcohol;baked',  // ''
    skip: skip, //on page load take the first 20 results . until phase 2 where we randomize this to rotate and feature different companies
    take: take,
  });

  await handleResponsePromiseAsync(
    getCoPackerFacilitiesPromise,
    (data) => {
      if (moreResults) {
        copackerFacilitiesList.value = copackerFacilitiesList.value.concat(
          data.ListItems as CopackerFacilityExtended[]
        );
      } else {
        copackerFacilitiesList.value =
          data.ListItems as CopackerFacilityExtended[];
      }

      totalItemsInList.value = data.TotalItemsInList;
      countFacilitiesDisplayed.value = copackerFacilitiesList.value.length;
    },
    "CopackerFacilities.vue",
    "GetCoPackerFacilities"
  );

  copackerFacilitiesLoading.value = false;
}

GetCoPackerFacilities();
GetCoPackerCategoriesAndLocationsForFilters();
getIsCopackerManagementAllowedAsync();

function GetAllCheckboxFilterValues() {
  FilterItemsValues = "";
  FilterItemsOperators = "";
  FilterItemsFields = "";
  LocationFilterItemsSelected = "";
  CategoryFilterItemsSelected = "";

  uniqueLocations.value.forEach((loc) => {
    if (loc.checked == true) LocationFilterItemsSelected += ";" + loc.label;
  }); // Get the checked parents
  uniqueLocations.value.forEach((loc) =>
    loc.children.forEach((child) => {
      if (child.checked == true)
        LocationFilterItemsSelected += ";" + loc.label + "-" + child.label;
    })
  ); // get the checked children
  uniqueCategories.value.forEach((cat) => {
    if (cat.checked == true) CategoryFilterItemsSelected += ";" + cat.label;
  }); // Get the checked parents
  uniqueCategories.value.forEach((cat) =>
    cat.children.forEach((child) => {
      if (child.checked == true)
        CategoryFilterItemsSelected += ";" + cat.label + "-" + child.label;
    })
  ); // get the checked children

  console.debug(
    "CopackerFacilities.vue: GetAllCheckboxFilterValues(): => LocationFilterItemsSelected: " +
      LocationFilterItemsSelected
  );
  console.debug(
    "CopackerFacilities.vue: GetAllCheckboxFilterValues(): => CategoryFilterItemsSelected: " +
      CategoryFilterItemsSelected
  );
}

function filterCoPackerSearch() {
  console.debug(
    "CopackerFacilities.vue: filterCoPackerSearch(): search term: " +
      txtInput.value
  );

  let lastCharInString = "";

  // if it's a location filter and matches a US State name, flip it to the US State Code
  states.forEach((x) => {
    if (
      LocationFilterItemsSelected.toLowerCase().includes(x.name.toLowerCase())
    ) {
      LocationFilterItemsSelected =
        LocationFilterItemsSelected.toUpperCase().replace(
          x.name.toUpperCase(),
          x.abbreviation
        );
      console.debug(
        "CopackerFacilities.vue: filterCoPackerSearch(): StateCodes: " +
          x.name +
          "/" +
          x.abbreviation
      );
    }
  }); // add in the full name if the single bar search is NJ, for example and the reverse as well

  //Remove any leading or trailing semicolon from the values
  if (
    CategoryFilterItemsSelected.length > 0 ||
    LocationFilterItemsSelected.length > 0
  ) {
    if (CategoryFilterItemsSelected.startsWith(";"))
      CategoryFilterItemsSelected = CategoryFilterItemsSelected.substring(1);
    if (LocationFilterItemsSelected.startsWith(";"))
      LocationFilterItemsSelected = LocationFilterItemsSelected.substring(1);

    //  add a corresponding location/category operator (all EQUAL filters for this checkbox filter type) and field name to the filter values.
    // although these are all equal operators so i'm hardcoding here. This is for use in generic filtering which we cant use (yet?) anyway
    // sort ORDER matters here. every one of these items must be listed in the same order in the values, operators and fields semi-colon delimited strings for the filter to work
    if (
      CategoryFilterItemsSelected.length > 0 ||
      LocationFilterItemsSelected.length > 0
    ) {
      FilterItemsOperators = ""; // reset values
      FilterItemsFields = ""; // reset values

      if (CategoryFilterItemsSelected.length > 0) {
        // b/c the length of the split will be 1 if there's 0 or 1 item
        for (
          let i = 0;
          i < CategoryFilterItemsSelected.split(";").length;
          i++
        ) {
          FilterItemsOperators += "eq" + ";";
          FilterItemsFields += "Category" + ";";
        }
      }

      if (LocationFilterItemsSelected.length > 0) {
        // b/c the length of the split will be 1 if there's 0 or 1 item
        for (
          let i = 0;
          i < LocationFilterItemsSelected.split(";").length;
          i++
        ) {
          FilterItemsOperators += "eq" + ";";
          FilterItemsFields += "Location" + ";";
        }
      }
    }

    // now concat the values for all filter types into one field
    FilterItemsValues = CategoryFilterItemsSelected.concat(
      ";",
      LocationFilterItemsSelected
    );
    FilterItemsValues.trim;

    //remove any leading or trailing semicolon from these strings so the indexing matches up in the service itself
    if (FilterItemsValues.length > 0) {
      if (FilterItemsOperators.substring(0, 1) == ";")
        FilterItemsOperators = FilterItemsOperators.substring(1);
      if (FilterItemsFields.substring(0, 1) == ";")
        FilterItemsFields = FilterItemsFields.substring(1);
      if (FilterItemsValues.substring(0, 1) == ";")
        FilterItemsValues = FilterItemsValues.substring(1);
      lastCharInString = FilterItemsOperators.substring(
        FilterItemsOperators.length - 1,
        FilterItemsOperators.length
      );
      if (lastCharInString === ";")
        FilterItemsOperators = FilterItemsOperators.slice(0, -1);
      lastCharInString = FilterItemsFields.substring(
        FilterItemsFields.length - 1,
        FilterItemsFields.length
      );
      if (lastCharInString === ";")
        FilterItemsFields = FilterItemsFields.slice(0, -1);
      lastCharInString = FilterItemsValues.substring(
        FilterItemsValues.length - 1,
        FilterItemsValues.length
      );
      if (lastCharInString === ";")
        FilterItemsValues = FilterItemsValues.slice(0, -1);
    }
    console.debug(
      "CopackerFacilities.vue: filterCoPackerSearch(): FilterItemsOperators: " +
        FilterItemsOperators
    );
    console.debug(
      "CopackerFacilities.vue: filterCoPackerSearch() FilterItemsFields: " +
        FilterItemsFields
    );
    console.debug(
      "CopackerFacilities.vue: filterCoPackerSearch() FilterItemsValues: " +
        FilterItemsValues
    );
  }

  // this gets execute for both single search bar and also sidebar filters. However the rest of this method is only for sidebar filters
  seeMoreCounterMainGrid = 20; // reset the counter for the see more button
  GetCoPackerFacilities(
    0,
    seeMoreCounterMainGrid,
    FilterItemsFields,
    FilterItemsOperators,
    FilterItemsValues
  );
}

async function seeMoreMainGridAsync() {
  console.debug(
    "CopackerFacilities.vue: seeMoreMainGrid(): " + seeMoreCounterMainGrid
  );
  const skip = seeMoreCounterMainGrid;
  seeMoreCounterMainGrid += defaultTake;
  await GetCoPackerFacilities(
    skip,
    defaultTake,
    FilterItemsFields,
    FilterItemsOperators,
    FilterItemsValues,
    true
  );
}

function SelectOrDeselectChildFilters(value: string) {
  // if a parent is checked always check all the children
  // if a parent is NOT checked, only uncheck if that is the parent that was clicked
  uniqueLocations.value.forEach((loc) =>
    loc.children.forEach((child) => {
      if (loc.checked == true) {
        child.checked = true;
      } else {
        if (loc.label == value) child.checked = false;
      }
    })
  ); // update all children to parent checked status
  uniqueCategories.value.forEach((cat) =>
    cat.children.forEach((child) => {
      if (cat.checked == true) {
        child.checked = true;
      } else {
        if (cat.label == value) child.checked = false;
      }
    })
  ); // check all children if parent is checked
}

// this can only be called on click because we only want to change child filter selections if they actually clicked on the parent
function RecalculateFilterSelections(
  checked: boolean,
  value: string,
  type: string,
  filterTier: string
) {
  console.debug(
    "CopackerFacilities.vue: RecalculateFilterSelections(): FilterContainer.vue - FilterType: " +
      type +
      " / " +
      value +
      "=>" +
      checked +
      "(" +
      filterTier +
      ")"
  );

  if (filterTier == "parent") {
    SelectOrDeselectChildFilters(value);
  }

  GetAllCheckboxFilterValues();
  filterCoPackerSearch();
}

function ClearAllFilters() {
  console.debug("CopackerFacilities.vue: clearAllFilters():");
  txtInput.value = "";
  FilterItemsFields = "";
  FilterItemsOperators = "";
  FilterItemsValues = "";
  seeMoreCounterMainGrid = defaultTake; //reset back to 20 on clearAll

  uniqueCategories.value.forEach((cat) => {
    cat.checked = false;
    cat.children.forEach((c) => (c.checked = false));
  });
  uniqueLocations.value.forEach((loc) => {
    loc.checked = false;
    loc.children.forEach((l) => (l.checked = false));
  });

  GetAllCheckboxFilterValues();
  filterCoPackerSearch();
}

function addNewClicked() {
  editFormFacility.value = undefined;
  showFacilityForm.value = true;
}

function editClicked(facility: CoPackerFacilities) {
  editFormFacility.value = facility;
  showFacilityForm.value = true;

  setTimeout(() => {
    const formElement = document.getElementById("facilityForm");
    formElement?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 100);
}

async function deleteFacilityAsync(facility: CopackerFacilityExtended) {
  await handleResponsePromiseAsync(
    api.get("DeleteCoPackerFacility", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      companyId: facility.company.CompanyId,
    }),
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.success,
        message: "Facility has been deleted successfully",
        duration: 5000,
      });

      copackerFacilitiesList.value = copackerFacilitiesList.value.filter(
        (x) => x.id !== facility.id
      );
    },
    "CopackerFacilities.vue",
    "deleteFacilityAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Failed to delete facility. Please try again later",
        duration: 5000,
      });
    }
  );
  facility.showDeleteConfirmation = false;
}

async function getIsCopackerManagementAllowedAsync() {
  return handleResponsePromiseAsync(
    api.get("IsCopackerManagementAllowed", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
    }),
    (data) => {
      canModifyFacilities.value = data;
    },
    "CopackerFacilities.vue",
    "getIsCopackerManagementAllowedAsync"
  );
}

function ClearThisFilter(value: string) {
  console.debug("CopackerFacilities.vue: ClearThisFilter(): " + value);

  // if any single filter is cleared, do a full teardown and reset all filters fields.
  // And then rebuild the filterItemValues from the checked items on the alternate filter

  if (value == "Industry") {
    uniqueCategories.value.forEach((cat) => {
      cat.checked = false;
      cat.children.forEach((c) => (c.checked = false));
    }); // remove the checkbox selections on this filter
  } else if (value == "Location") {
    uniqueLocations.value.forEach((loc) => {
      loc.checked = false;
      loc.children.forEach((l) => (l.checked = false));
    }); // remove the checkbox selections on this filter
  }

  GetAllCheckboxFilterValues();
  filterCoPackerSearch();
}
</script>

<style scoped lang="scss">
header.page-header {
  background: #eef4f6;
  padding: 30px 30px 20px;
  margin: 0;
  border: none;

  @media only screen and (min-width: 740px) {
    display: flex;
  }
}

.contact-text {
  font-size: 12px;
  font-weight: normal;
  color: #3c3a3a;
  margin-left: 2rem;
  padding: 15px 30px;
  background: #e1edf1;
  margin: 0px 0px 0px 0px;
}

.header-text {
  color: #3a5da6;
  font-size: 22px;
  font-weight: bold;
  margin-right: 40px;

  .results-label {
    font-size: 14px;
    display: block;
    font-weight: normal;
    color: #444;
  }
}

.header-input {
  flex: 1;
  max-width: 800px;
  position: relative;

  .search-icon {
    cursor: pointer;
    position: absolute;
    top: 10px;
    right: 10px;
  }

  input {
    width: 100%;
    height: 50px;
    box-shadow: 0 0px 10px rgba(0, 0, 0, 0.05);

    &:focus {
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      border-color: #3a5da6;
    }
  }
}

.clear-input {
  display: block;
  margin-top: 10px;
  cursor: pointer;
  color: #666;
  text-decoration: underline;
}

.page-container {
  padding: 30px 30px 60px;
  background: white;
  min-height: 100%;

  @media only screen and (min-width: 740px) {
    display: flex;
  }

  .sidebar {
    flex: 1;
    max-width: 350px;
    margin: 0;
    padding: 0;
    margin-right: 40px;
  }

  .main-content {
    flex: 3;
  }
}

.add-new-facility-button {
  margin-bottom: 20px;
}

.result-item,
.facility-form {
  margin-bottom: 20px;
  display: flex;
  box-shadow: 0 0 35px rgb(0 0 0 / 10%);
  position: relative;
}

.result-delete {
  &__overlay {
    width: 100%;
    position: absolute;
    height: 100%;
    z-index: 2;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
    box-shadow: 0 0 35px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
  }

  &__close {
    height: 20px;
    color: white;
    position: absolute;
    top: 12px;
    right: 12px;
    cursor: pointer;

    &:hover {
      color: #ccc;
    }
  }

  &__content-text {
    color: white;
    font-size: 18px;
    font-weight: bold;
    text-align: center;
    max-width: 500px;
  }

  &__content-actions {
    display: flex;
    gap: 18px;

    .k-button {
      width: 100px;
      font-weight: bold;
    }
  }
}

.result-main {
  flex: 3;
  padding: 20px;

  > header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }

  h2 {
    font-size: 20px;
    font-weight: bold;
  }

  .location {
    color: #999;
    margin-left: 10px;
    margin-top: 3px;

    span:not(:last-child):after {
      content: ",";
    }
  }

  .description {
    display: flex;
  }
}

.result-contact {
  flex: 1;
  background: #f7f7f7;
  border-left: 1px solid #efefef;
  padding: 20px;
  min-width: 300px;
  max-width: 300px;
  word-break: break-all;

  &-header {
    display: flex;
    justify-content: space-between;

    &__actions {
      display: flex;
      gap: 10px;
    }
  }

  .edit-button {
    cursor: pointer;
    color: #399cd3;
    height: 18px;

    &:hover {
      color: #3a5da6;
    }
  }

  .delete-button {
    cursor: pointer;
    color: #dc3545;
    height: 18px;

    &:hover {
      color: red;
    }
  }

  .contact-name {
    font-weight: bold;
    font-size: 15px;
    color: #666;
  }

  a,
  span {
    display: block;
    margin-bottom: 3px;
  }
}

.more-results {
  text-align: right;
  padding-top: 20px;
  border-top: 3px solid #efefef;

  a {
    cursor: pointer;
    color: #3a5da6;
    display: inline-block;
    padding: 8px 30px;
    box-shadow: 0 0 15px rgb(0 0 0 / 10%);
    font-weight: bold;
    font-size: 18px;

    &:hover {
      background: #efefef;
    }
  }
}

section.no-results {
  text-align: center;
  padding-top: 40px;
  color: #666;
}

/* tooltip */
.help-tooltip {
  position: relative;
  display: inline-block;
  z-index: 10;
}

.fa-question-circle {
  font-size: 16px;
  display: block;
  line-height: 30px;
  color: #399cd3;
  cursor: pointer;
}

.fa-question-circle:hover ~ .tooltip-content {
  display: block;
}

.tooltip-content {
  display: none;
  background: #ffffe8;
  position: absolute;
  left: 20px;
  top: 3px;
  padding: 10px 15px;
  width: 300px;
  border: 1px solid #e4e4d5;
  border-radius: 4px;
  box-shadow: 1px 1px 10px rgb(0 0 0 / 10%);
  font-size: 14px;
  font-weight: normal;
  color: #222;
}

// Some animations for delete overlay
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
